import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Rocket, RefreshCw } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function AdvisorPanel() {
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    api.get("/org/assessments")
      .then((r) => { setCurrent(r.data.assessments[0] || null); setFetched(true); })
      .catch(() => setFetched(true));
  }, []);

  const generate = async (force) => {
    setLoading(true);
    try {
      const { data } = await api.post("/org/assessments/generate", { force });
      setCurrent(data);
      toast.success(data.cached ? "Loaded cached assessment (inputs unchanged)" : "Assessment generated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const a = current?.assessment;

  return (
    <div className="space-y-5" data-testid="advisor-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <p className="text-[13px] text-muted-foreground max-w-2xl">
          Scaling-readiness assessment generated from aggregated, de-identified org statistics only — every
          payload passes through the masked AI gateway and is audit-logged. Results are cached until the
          underlying organization changes.
        </p>
        <div className="flex gap-2 shrink-0">
          <Button onClick={() => generate(false)} disabled={loading} data-testid="advisor-generate-btn"
            className="rounded-none bg-teal-700 hover:bg-teal-800 text-white h-9 text-[13px]">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
            Generate assessment
          </Button>
          {current && (
            <Button onClick={() => generate(true)} disabled={loading} variant="outline" data-testid="advisor-force-btn"
              className="rounded-none h-9 text-[13px]">
              <RefreshCw className="h-4 w-4" /> Force refresh
            </Button>
          )}
        </div>
      </div>

      {loading && (
        <div className="border border-border bg-white p-6 text-[13px] text-muted-foreground" data-testid="advisor-loading">
          Consulting engine running — masked payload sent to the model. This takes 15–45 seconds…
        </div>
      )}

      {!loading && fetched && !current && (
        <div className="border border-border bg-white p-6 text-[13px] text-muted-foreground">
          No assessment yet. Generate the first scaling-readiness memo.
        </div>
      )}

      {!loading && a && (
        <div className="space-y-4 fade-in">
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
            <div className="bg-[var(--brand-primary)] text-white p-6" data-testid="advisor-score">
              <p className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold">Scaling readiness</p>
              <p className="text-5xl font-bold mt-2">{a.scaling_readiness_score}<span className="text-lg text-slate-400 font-normal">/100</span></p>
              <div className="h-1.5 bg-white/10 mt-4">
                <div className="h-1.5 bg-teal-500" style={{ width: `${a.scaling_readiness_score}%` }} />
              </div>
              <p className="text-[10px] font-mono text-slate-400 mt-4">
                {current.model}<br />audit {current.audit_id?.slice(0, 8)} · {current.cached ? "cached" : "fresh"}
              </p>
            </div>
            <div className="bg-white border border-border p-6">
              <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">Executive summary</p>
              <p className="text-[13.5px] text-slate-800 leading-relaxed mt-2" data-testid="advisor-summary">{a.summary}</p>
              <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mt-5">Benchmark commentary</p>
              <p className="text-[13px] text-slate-600 leading-relaxed mt-2">{a.benchmark_commentary}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-border p-6">
              <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-3">Target structure by function</p>
              <div className="divide-y divide-border border border-border">
                {(a.target_structure || []).map((t, i) => (
                  <div key={i} className="px-3 py-2.5 grid grid-cols-[130px_1fr] gap-3">
                    <span className="text-[12px] font-semibold text-slate-800">{t.function}</span>
                    <span className="text-[12px] text-slate-600 leading-relaxed">{t.recommendation}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              <div className="bg-white border border-border p-6">
                <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-2.5">Roles to add</p>
                <div className="flex flex-wrap gap-1.5">
                  {(a.roles_to_add || []).map((r, i) => (
                    <span key={i} className="text-[11px] bg-teal-50 text-teal-800 border border-teal-600/30 px-2 py-1">{r}</span>
                  ))}
                </div>
              </div>
              <div className="bg-white border border-border p-6">
                <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-2.5">Roles to merge / flatten</p>
                <div className="flex flex-wrap gap-1.5">
                  {(a.roles_to_merge || []).map((r, i) => (
                    <span key={i} className="text-[11px] bg-slate-100 text-slate-700 border border-border px-2 py-1">{r}</span>
                  ))}
                  {!(a.roles_to_merge || []).length && <span className="text-[12px] text-muted-foreground">None recommended.</span>}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-border p-6" data-testid="advisor-sequencing">
            <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-3">Sequencing plan</p>
            <div className="space-y-0 divide-y divide-border border border-border">
              {(a.sequencing || []).map((s) => (
                <div key={s.step} className="flex gap-4 px-4 py-3">
                  <span className="h-6 w-6 shrink-0 bg-[var(--brand-primary)] text-white text-[11px] font-bold flex items-center justify-center">{s.step}</span>
                  <div>
                    <p className="text-[13px] font-semibold text-slate-800">{s.action}</p>
                    <p className="text-[12px] text-slate-500 leading-relaxed mt-0.5">{s.rationale}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[10px] font-mono text-muted-foreground mt-3">
              generated {current.created_at?.slice(0, 16).replace("T", " ")} UTC by {current.created_by} · de-identified aggregate payload · gateway audit {current.audit_id}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
